import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Truck, Plus, Trash, X, PencilSimple, Clock, Buildings, Package, UserCircle, CheckCircle } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useSearch } from '../VerticalTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUS = {
  a_commander: { label: 'A commander', color: '#C62828', bg: '#FFEBEE' },
  commandee: { label: 'Commandee', color: '#EF6C00', bg: '#FFF3E0' },
  recue: { label: 'Recue', color: '#2E7D32', bg: '#E8F5E9' },
};

export const OrdersTab = ({ projectFilter = null } = {}) => {
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({ project_id: '', description: '', supplier: '', deadline: '', delivery_delay: '', quantity: 1, unit: '', status: 'a_commander', notes: '' });
  const [dispatchOrder, setDispatchOrder] = useState(null); // {order, target, project_id, price_ht, tva_rate}
  const [suppliers, setSuppliers] = useState([]);
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, projRes, supRes] = await Promise.all([
        axios.get(`${API_URL}/api/orders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/suppliers`, { withCredentials: true }),
      ]);
      setOrders(ordersRes.data);
      setProjects(projRes.data);
      setSuppliers(supRes.data || []);
    } catch (e) { toast.error('Erreur chargement commandes'); }
    finally { setLoading(false); }
  }, []);

  const handleCreateSupplier = async () => {
    const name = (newSupplierName || '').trim();
    if (!name) return;
    try {
      const r = await axios.post(`${API_URL}/api/suppliers`, { name }, { withCredentials: true });
      const created = r.data;
      setSuppliers((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((f) => ({ ...f, supplier: created.name }));
      toast.success('Fournisseur créé');
      setCreatingSupplier(false);
      setNewSupplierName('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création fournisseur');
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editOrder) {
        await axios.put(`${API_URL}/api/orders/${editOrder.id}`, form, { withCredentials: true });
        toast.success('Commande modifiee');
      } else {
        await axios.post(`${API_URL}/api/orders`, form, { withCredentials: true });
        toast.success('Commande ajoutee');
      }
      setShowModal(false); setEditOrder(null);
      setForm({ project_id: '', description: '', supplier: '', deadline: '', delivery_delay: '', quantity: 1, unit: '', status: 'a_commander', notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try { await axios.delete(`${API_URL}/api/orders/${id}`, { withCredentials: true }); toast.success('Commande supprimee'); fetchData(); } catch (e) { toast.error('Erreur'); }
  };

  const handleDispatchSubmit = async () => {
    if (!dispatchOrder) return;
    const payload = { target: dispatchOrder.target };
    if (dispatchOrder.target === 'project') {
      if (!dispatchOrder.project_id) { toast.error('Selectionnez un chantier'); return; }
      payload.project_id = dispatchOrder.project_id;
    }
    if (dispatchOrder.price_ht) payload.price_ht = parseFloat(dispatchOrder.price_ht);
    if (dispatchOrder.tva_rate) payload.tva_rate = parseFloat(dispatchOrder.tva_rate);
    if (dispatchOrder.unit) payload.unit = dispatchOrder.unit;
    try {
      await axios.post(`${API_URL}/api/orders/${dispatchOrder.order.id}/dispatch`, payload, { withCredentials: true });
      toast.success(dispatchOrder.target === 'stock' ? 'Ajoute aux stocks' : 'Affecte au chantier');
      setDispatchOrder(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur dispatch'); }
  };

  const openEdit = (order) => {
    setEditOrder(order);
    setForm({ project_id: order.project_id || '', description: order.description, supplier: order.supplier, deadline: order.deadline, delivery_delay: order.delivery_delay, quantity: order.quantity, unit: order.unit, status: order.status, notes: order.notes || '' });
    setShowModal(true);
  };

  const openAdd = () => { setEditOrder(null); setForm({ project_id: projectFilter || '', description: '', supplier: '', deadline: '', delivery_delay: '', quantity: 1, unit: '', status: 'a_commander', notes: '' }); setShowModal(true); };
  const isOverdue = (deadline) => deadline && new Date(deadline) < new Date();
  const { searchTerm } = useSearch();

  const filtered = orders.filter(o => {
    if (projectFilter && o.project_id !== projectFilter) return false;
    if (!projectFilter && filterProject !== 'all' && o.project_id !== filterProject) return false;
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    if (searchTerm) { const s = searchTerm.toLowerCase(); if (!(o.description || '').toLowerCase().includes(s) && !(o.supplier || '').toLowerCase().includes(s) && !(o.project_name || '').toLowerCase().includes(s)) return false; }
    return true;
  });

  const urgentCount = orders.filter(o => o.status === 'a_commander' && isOverdue(o.deadline)).length;
  const toOrderCount = orders.filter(o => o.status === 'a_commander').length;
  const orderedCount = orders.filter(o => o.status === 'commandee').length;
  const employeeReqCount = orders.filter(o => o.source === 'employee' && o.status !== 'recue').length;

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: '20px', gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card" data-testid="orders-urgent"><div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#C62828' }}><Clock size={16} /> Urgentes (en retard)</div><div className="stat-value" style={{ color: '#C62828' }}>{urgentCount}</div></div>
        <div className="stat-card" data-testid="orders-employee-requests"><div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#7B1FA2' }}><UserCircle size={16} /> Demandes salariés</div><div className="stat-value" style={{ color: '#7B1FA2' }}>{employeeReqCount}</div></div>
        <div className="stat-card" data-testid="orders-to-order"><div className="stat-label">A commander</div><div className="stat-value" style={{ color: '#EF6C00' }}>{toOrderCount}</div></div>
        <div className="stat-card" data-testid="orders-ordered"><div className="stat-label">En cours de livraison</div><div className="stat-value" style={{ color: '#1565C0' }}>{orderedCount}</div></div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Select value={filterProject} onValueChange={setFilterProject}><SelectTrigger style={{ width: '200px' }}><SelectValue placeholder="Chantier" /></SelectTrigger><SelectContent><SelectItem value="all">Tous les chantiers</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger style={{ width: '180px' }}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tous les statuts</SelectItem><SelectItem value="a_commander">A commander</SelectItem><SelectItem value="commandee">Commandees</SelectItem><SelectItem value="recue">Recues</SelectItem></SelectContent></Select>
        </div>
        <button className="btn btn-primary" onClick={openAdd} data-testid="add-order-btn"><Plus size={16} /> Nouvelle commande</button>
      </div>

      <div className="card">
        {filtered.length > 0 ? (
          <table className="data-table" data-testid="orders-table">
            <thead><tr><th>Article</th><th>Chantier</th><th>Fournisseur</th><th>Qte</th><th>Date limite</th><th>Delai</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {filtered.map(o => {
                const overdue = o.status === 'a_commander' && isOverdue(o.deadline);
                const st = ORDER_STATUS[o.status] || ORDER_STATUS.a_commander;
                return (
                  <tr key={o.id} style={{ backgroundColor: overdue ? '#FFF5F5' : (o.source === 'employee' && o.status !== 'recue' ? '#F3E5F5' : 'transparent') }}>
                    <td>
                      <div style={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {o.source === 'employee' && <UserCircle size={14} color="#7B1FA2" weight="fill" title="Demande salarie" />}
                        {o.description}
                      </div>
                      {o.requested_by_name && <div style={{ fontSize: '0.72rem', color: '#7B1FA2', fontWeight: '600' }}>Demande par {o.requested_by_name}</div>}
                      {o.notes && <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{o.notes}</div>}
                    </td>
                    <td>{o.project_name}</td><td style={{ fontWeight: '600' }}>{o.supplier || '-'}</td><td>{o.quantity} {o.unit}</td>
                    <td style={{ fontWeight: '600', color: overdue ? '#C62828' : 'inherit' }}>{o.deadline || '-'}{overdue && <span style={{ display: 'block', fontSize: '0.7rem', color: '#C62828' }}>EN RETARD</span>}</td>
                    <td>{o.delivery_delay || '-'}</td>
                    <td><span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: st.bg, color: st.color }}>{st.label}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {o.source === 'employee' && o.status !== 'recue' && (
                          <button className="btn btn-primary btn-sm" style={{ backgroundColor: '#7B1FA2' }} data-testid={`dispatch-order-${o.id}`}
                                  title="Affecter (stock / chantier)"
                                  onClick={() => setDispatchOrder({ order: o, target: 'stock', project_id: '', unit: o.unit, price_ht: '', tva_rate: 20 })}>
                            <CheckCircle size={14} /> Affecter
                          </button>
                        )}
                        {o.status === 'a_commander' && (<button className="btn btn-primary btn-sm" onClick={async () => { await axios.put(`${API_URL}/api/orders/${o.id}`, { status: 'commandee' }, { withCredentials: true }); toast.success('Marquee commandee'); fetchData(); }} data-testid={`mark-ordered-${o.id}`}><Truck size={14} /></button>)}
                        {o.status === 'commandee' && (<button className="btn btn-primary btn-sm" style={{ backgroundColor: '#2E7D32' }} onClick={async () => { await axios.put(`${API_URL}/api/orders/${o.id}`, { status: 'recue' }, { withCredentials: true }); toast.success('Marquee recue'); fetchData(); }} data-testid={`mark-received-${o.id}`}><Buildings size={14} /></button>)}
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(o)} data-testid={`edit-order-${o.id}`}><PencilSimple size={14} /></button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(o.id)}><Trash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}><Truck size={48} style={{ marginBottom: '12px', opacity: 0.5 }} /><p>Aucune commande a passer</p></div>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
              <h3 style={{ margin: 0 }}>{editOrder ? 'Modifier la commande' : 'Nouvelle commande'}</h3>
              <button onClick={() => { setShowModal(false); setEditOrder(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div className="form-group"><label className="form-label">Chantier</label><Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}><SelectTrigger data-testid="order-project-select"><SelectValue placeholder="Selectionner un chantier" /></SelectTrigger><SelectContent className="z-[9999]"><SelectItem value="none">Aucun chantier</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="form-group"><label className="form-label">Description de l'article *</label><input type="text" className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="order-description-input" /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Fournisseur</label>
                  {creatingSupplier ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text" className="form-input"
                        placeholder="Nom du nouveau fournisseur"
                        value={newSupplierName}
                        autoFocus
                        onChange={(e) => setNewSupplierName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateSupplier(); } }}
                        data-testid="new-supplier-name-input"
                      />
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateSupplier} data-testid="confirm-new-supplier">OK</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCreatingSupplier(false); setNewSupplierName(''); }}>X</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Select value={form.supplier || '__none__'} onValueChange={(v) => setForm({ ...form, supplier: v === '__none__' ? '' : v })}>
                        <SelectTrigger data-testid="order-supplier-select" style={{ flex: 1 }}>
                          <SelectValue placeholder="Aucun" />
                        </SelectTrigger>
                        <SelectContent className="z-[9999]">
                          <SelectItem value="__none__">Aucun</SelectItem>
                          {suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreatingSupplier(true)} title="Créer un nouveau fournisseur" data-testid="new-supplier-btn">
                        <Plus size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group"><label className="form-label">Date limite de commande</label><input type="date" className="form-input" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="order-deadline-input" /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Quantite</label><input type="number" className="form-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })} min={1} /></div>
                <div className="form-group"><label className="form-label">Unite</label><input type="text" className="form-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kg, m, pcs..." /></div>
                <div className="form-group"><label className="form-label">Delai de livraison</label><input type="text" className="form-input" value={form.delivery_delay} onChange={(e) => setForm({ ...form, delivery_delay: e.target.value })} placeholder="Ex: 5 jours, 2 sem." data-testid="order-delay-input" /></div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Informations complementaires..." /></div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditOrder(null); }} style={{ flex: 1 }}>Annuler</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-order-btn">{editOrder ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dispatchOrder && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '10px', width: '100%', maxWidth: '540px', padding: '24px' }} data-testid="dispatch-modal">
            <h3 style={{ margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle size={20} color="#7B1FA2" weight="fill" /> Affecter la demande</h3>
            <div style={{ padding: '10px 12px', background: '#F3E5F5', borderRadius: '6px', marginBottom: '14px', fontSize: '0.85rem' }}>
              <strong>{dispatchOrder.order.description}</strong>
              <div style={{ color: '#5E5F66', marginTop: '2px' }}>{dispatchOrder.order.quantity} {dispatchOrder.order.unit} · demandé par {dispatchOrder.order.requested_by_name}</div>
            </div>
            <div className="form-group">
              <label className="form-label">Destination *</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className={dispatchOrder.target === 'stock' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        onClick={() => setDispatchOrder({ ...dispatchOrder, target: 'stock' })} data-testid="dispatch-stock-btn">
                  <Package size={16} /> Ajouter aux stocks
                </button>
                <button type="button" className={dispatchOrder.target === 'project' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        onClick={() => setDispatchOrder({ ...dispatchOrder, target: 'project' })} data-testid="dispatch-project-btn">
                  <Buildings size={16} /> Affecter à un chantier
                </button>
              </div>
            </div>
            {dispatchOrder.target === 'project' && (
              <div className="form-group">
                <label className="form-label">Chantier *</label>
                <select className="form-input" value={dispatchOrder.project_id} onChange={(e) => setDispatchOrder({ ...dispatchOrder, project_id: e.target.value })} data-testid="dispatch-project-select">
                  <option value="">Sélectionner un chantier</option>
                  {projects.filter(p => !p.archived).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Unité</label>
                <input className="form-input" value={dispatchOrder.unit || ''} onChange={(e) => setDispatchOrder({ ...dispatchOrder, unit: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Prix HT unitaire</label>
                <input type="number" min="0" step="0.01" className="form-input" value={dispatchOrder.price_ht || ''} onChange={(e) => setDispatchOrder({ ...dispatchOrder, price_ht: e.target.value })} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Taux TVA</label>
                <select className="form-input" value={dispatchOrder.tva_rate} onChange={(e) => setDispatchOrder({ ...dispatchOrder, tva_rate: e.target.value })}>
                  <option value={20}>20%</option><option value={10}>10%</option><option value={5.5}>5.5%</option><option value={0}>0%</option>
                </select>
              </div>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginBottom: '12px' }}>
              Le matériau sera ajouté au catalogue s'il n'existe pas. {dispatchOrder.target === 'project' && 'Une dépense sera créée automatiquement sur le chantier.'}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDispatchOrder(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleDispatchSubmit} data-testid="dispatch-submit-btn"
                      style={{ backgroundColor: '#7B1FA2' }}>Confirmer l'affectation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
